
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Typography,
    Paper,
    TextField,
    Button,
    Box,
    FormControl,
    FormLabel,
    RadioGroup,
    FormControlLabel,
    Radio,
    Alert,
    CircularProgress,
    Tooltip,
    IconButton,
    ToggleButton,
    ToggleButtonGroup,
    Divider,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Switch
} from '@mui/material';
import { useSelector } from 'react-redux';
import InfoIcon from '@mui/icons-material/Info';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
// Removed multi-phone support icons (Add/Remove) since only one phone number is supported now
import AdminNav from '../AdminNav/AdminNav';
import api from '../../utils/axiosConfig';



const AddCaregiver = () => {
    const { customerKey } = useParams();
    const navigate = useNavigate();
    const smartConnectUnit = useSelector((state) => {
        const mc = state.auth.activeUnit?.MC || "";
        return /smart connect/i.test(mc);
    });
    const [loading, setLoading] = useState(false);
    const [loadingTemplates, setLoadingTemplates] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const UnitSerNum = useSelector((state) => state.auth.activeUnit?.UnitSerNum);
    const DealerName = useSelector((state) => state.auth.activeUnit?.DealerName) || '';
    const ConnectContactEmail = useSelector((state) => state.auth.activeUnit?.ConnectContactEmail) || '';
    const ActivePhoneNumber = useSelector((state) => state.auth.activeUnit?.ActivePhoneNumber || '');
    const [availableTemplates, setAvailableTemplates] = useState([]);
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        userType: 'View', // Default to View for safety
        password: '',
        callContact: smartConnectUnit ? 1 : 0,
        phoneNumbers: [''], // single formatted display value ( (XXX)XXX-XXXX )
        useApp: 1, // Default to 1 (yes) for Connect App 365 Access
    });
    const [preferences, setPreferences] = useState([]);
    const [fieldErrors, setFieldErrors] = useState({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        phoneNumbers: ''
    });

    // Fetch available notification templates and set up default preferences
    useEffect(() => {
        const fetchTemplates = async () => {
            try {
                setLoadingTemplates(true);
                // Fetch templates from the notification preferences endpoint with useApp and smartConnectUnit params
                const response = await api.get(`/api/notification-templates`, {
                    params: {
                        useApp: formData.useApp,
                        smartConnectUnit: smartConnectUnit
                    }
                });
                const allTemplates = response.data.templates || [];
                
                // Filter templates for DISPLAY only (what to show in UI)
                const templatesForDisplay = allTemplates.filter(tmpl => {
                    // Hide welcome_new_caregiver - it's a one-time notification
                    if (tmpl.TemplateName === 'welcome_new_caregiver') {
                        return false;
                    }
                    // Show smart_connect_alert_received for everyone, but hide other smart_connect templates for non-SmartConnect units
                    if (tmpl.TemplateName.includes('smart_connect') && tmpl.TemplateName !== 'smart_connect_alert_received' && !smartConnectUnit) {
                        return false;
                    }
                    // For non-app users, only show templates flagged as ShowForNonAppUsers
                    if (formData.useApp === 0 && !tmpl.ShowForNonAppUsers) {
                        return false;
                    }
                    return true;
                });
                
                setAvailableTemplates(templatesForDisplay);
                
                // Create preferences for ALL templates (including ones not displayed in UI)
                // This ensures we send all preferences to backend, even if they're not visible
                const allPreferences = allTemplates
                    .filter(tmpl => tmpl.TemplateName !== 'welcome_new_caregiver') // Only exclude one-time notifications
                    .map(template => ({
                        Category: template.Category,
                        TemplateName: template.TemplateName,
                        EmailEnabled: template.EmailEnabled,
                        // New caregivers start with push channel preference disabled.
                        PushEnabled: 0,
                        InAppEnabled: template.InAppEnabled,
                        SMSEnabled: template.SMSEnabled,
                        QuietHoursStart: template.QuietHoursStart,
                        QuietHoursEnd: template.QuietHoursEnd
                    }));
                
                setPreferences(allPreferences);
                setLoadingTemplates(false);
            } catch (error) {
                console.error('Error fetching notification templates:', error);
                setError('Failed to load notification templates');
                setLoadingTemplates(false);
            }
        };
        
        fetchTemplates();
    }, [smartConnectUnit, formData.useApp]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value
        });
    };

    const formatAndValidatePhoneNumbers = () => {
        if (!formData.phoneNumbers || formData.phoneNumbers.length === 0) {
            return { isValid: true, values: [] }; // optional if left blank
        }
        const errors = [];
        const transformed = [];
        formData.phoneNumbers.forEach((raw, idx) => {
            const digits = (raw || '').replace(/\D/g, '');
            if (digits.length === 0) return; // skip blank
            if (digits.length !== 10) {
                errors.push(`Phone Number must have exactly 10 digits`); // add this to the error once we allow for more than one phone number ${idx + 1} 
            } else {
                transformed.push('1' + digits); // Add leading 1
            }
        });
        return { isValid: errors.length === 0, errors, values: transformed };
    };

    const handleTogglePreference = (templateName, category, field) => {
        setPreferences(prevPrefs => 
            prevPrefs.map(p =>
                p.TemplateName === templateName && p.Category === category
                    ? { ...p, [field]: p[field] ? 0 : 1 }
                    : p
            )
        );
    };

    const getPreferenceValue = (templateName, category, field) => {
        const pref = preferences.find(
            p => p.TemplateName === templateName && p.Category === category
        );
        return pref ? !!pref[field] : false;
    };

    const getCategoryLabel = (category) => {
        const labels = {
            'system': 'System Notifications',
            'caregiver': 'Caregiver Activity',
            'device': 'Device Status',
            'alert': 'Alerts & Emergencies',
            'smart_connect': 'Smart Connect'
        };
        return labels[category] || category;
    };

    const deliveryMethodTooltips = {
        email: 'Send notifications to the caregiver\'s registered email address. They will receive detailed email notifications that they can review at their convenience.',
        push: 'Send instant push notifications to the caregiver\'s mobile device. These appear as pop-up alerts even when the app is not open.',
        sms: 'Send text message alerts to the caregiver\'s mobile phone. They will receive instant SMS notifications for critical emergency alerts.'
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setFieldErrors({
            firstName: '',
            lastName: '',
            email: '',
            password: '',
            phoneNumbers: ''
        });

        const errors = {};
        let hasErrors = false;

        if (!formData.firstName?.trim()) {
            errors.firstName = 'First Name is required';
            hasErrors = true;
        }
        if (!formData.lastName?.trim()) {
            errors.lastName = 'Last Name is required';
            hasErrors = true;
        }

        if (formData.useApp === 1) {
            if (!formData.email?.trim()) {
                errors.email = 'Email is required';
                hasErrors = true;
            } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
                errors.email = 'Invalid email format';
                hasErrors = true;
            }

            if (!formData.password?.trim()) {
                errors.password = 'Password is required';
                hasErrors = true;
            } else if (formData.password.length < 6) {
                errors.password = 'Password must be at least 6 characters';
                hasErrors = true;
            }
        }
        if (smartConnectUnit) {
            const invalidNumbers = formData.phoneNumbers.filter(num => num.replace(/\D/g, '').length !== 10);
            if (invalidNumbers.length > 0) {
                errors.phoneNumbers = 'All phone numbers must have exactly 10 digits';
                hasErrors = true;
            }
        }

        const phoneValidation = formatAndValidatePhoneNumbers();
        if (!phoneValidation.isValid) {
            errors.phoneNumbers = phoneValidation.errors.join('\n');
            hasErrors = true;
        }

        if (hasErrors) {
            setFieldErrors(errors);
            return;
        }

        setLoading(true);
        
        try {
            await api.post(`/api/caregivers/${customerKey}`, {
                firstName: formData.firstName,
                lastName: formData.lastName,
                email: formData.useApp === 1 ? formData.email : null,
                userType: formData.useApp === 1 ? formData.userType : 'No Access',
                password: formData.useApp === 1 ? formData.password : null,
                callContact: formData.callContact,
                UnitSerNum: UnitSerNum,
                phoneNumbers: phoneValidation.values.length === 0 ? null : phoneValidation.values.join(','),
                smartConnectUnit,
                DealerName: DealerName,
                ConnectContactEmail: ConnectContactEmail,
                ActivePhoneNumber: ActivePhoneNumber,
                useApp: formData.useApp,
                preferences: preferences, // Send notification preferences
            });

            setSuccess(true);
            // Reset form after successful submission
            setFormData({
                firstName: '',
                lastName: '',
                email: '',
                password: '',
                userType: 'View',
                callContact: 0,
                phoneNumbers: [''],
                useApp: 1,
            });

            // Navigate back to caregiver list after short delay
            setTimeout(() => {
                navigate(`/caregivers/${customerKey}`);
            }, 2000);
        } catch (error) {
            console.error('Failed to add caregiver:', error);
            if (error.response && error.response.data && error.response.data.message) {
                setError(error.response.data.message);
            } else {
                setError('An error occurred while adding the caregiver. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div>
            <AdminNav customerKey={customerKey} />

            <Paper sx={{ maxWidth: "800px", margin: "0 auto", p: 3, mt: 2 }}>
                <Typography variant="h5" component="h1" gutterBottom>
                    Add New Caregiver
                </Typography>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {success && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                        Caregiver added successfully! Redirecting...
                    </Alert>
                )}

                <Box component="form" onSubmit={handleSubmit} noValidate>
                    {smartConnectUnit && (
                        <Box sx={{ mb: 3, p: 2, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <Typography variant="subtitle1" fontWeight="medium">
                                    Connect App 365 Access
                                </Typography>
                                <Tooltip 
                                    title="Select Yes if this caregiver will use Connect App 365. Select No if they will only receive calls and/or SMS without app access."
                                    placement="right"
                                    arrow
                                >
                                    <IconButton size="small">
                                        <HelpOutlineIcon fontSize="small" color="action" />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                            <ToggleButtonGroup
                                exclusive
                                size="medium"
                                color="primary"
                                value={formData.useApp}
                                onChange={(_, val) => {
                                    if (val === null) return;
                                    setFormData({
                                        ...formData,
                                        useApp: val,
                                    });
                                }}
                                disabled={loading}
                                fullWidth
                            >
                                <ToggleButton value={1}>Yes</ToggleButton>
                                <ToggleButton value={0}>No</ToggleButton>
                            </ToggleButtonGroup>
                        </Box>
                    )}

                    <TextField 
                        margin="normal" 
                        required 
                        fullWidth 
                        id="firstName" 
                        label="First Name" 
                        name="firstName" 
                        autoComplete="given-name" 
                        value={formData.firstName} 
                        onChange={handleChange} 
                        disabled={loading}
                        error={!!fieldErrors.firstName}
                        helperText={fieldErrors.firstName}
                    />

                    <TextField 
                        margin="normal" 
                        required 
                        fullWidth 
                        id="lastName" 
                        label="Last Name" 
                        name="lastName" 
                        autoComplete="family-name" 
                        value={formData.lastName} 
                        onChange={handleChange} 
                        disabled={loading}
                        error={!!fieldErrors.lastName}
                        helperText={fieldErrors.lastName}
                    />

                    {(!smartConnectUnit || formData.useApp === 1) && (
                        <>
                            <TextField 
                                margin="normal" 
                                required 
                                fullWidth 
                                id="email" 
                                label="Email Address" 
                                name="email" 
                                autoComplete="email" 
                                type="email" 
                                value={formData.email} 
                                onChange={handleChange} 
                                disabled={loading}
                                error={!!fieldErrors.email}
                                helperText={fieldErrors.email}
                            />

                            <TextField 
                                margin="normal" 
                                required 
                                fullWidth 
                                id="password" 
                                label="Temporary Password" 
                                name="password" 
                                autoComplete="new-password" 
                                type="text" 
                                value={formData.password} 
                                onChange={handleChange} 
                                disabled={loading}
                                error={!!fieldErrors.password}
                                helperText={fieldErrors.password}
                            />
                            <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
                                <Typography variant="caption" color="text.secondary" sx={{ display: "flex", alignItems: "center" }}>
                                    <InfoIcon fontSize="small" sx={{ mr: 0.5 }} />
                                    This password is temporary. The new caregiver will be prompted to change it when they first login.
                                </Typography>
                            </Box>
                        </>
                    )}

                    {smartConnectUnit && (
                        <>
                            <TextField
                                margin="normal"
                                required
                                fullWidth
                                id="phoneNumber-0"
                                label="Phone Number"
                                placeholder="(XXX)XXX-XXXX"
                                value={formData.phoneNumbers[0] || ""}
                                onChange={(e) => {
                                    const value = e.target.value.replace(/\D/g, "");
                                    let formattedValue = "";
                                    if (value.length > 0) {
                                        if (value.length <= 3) {
                                            formattedValue = `(${value}`;
                                        } else if (value.length <= 6) {
                                            formattedValue = `(${value.slice(0, 3)})${value.slice(3)}`;
                                        } else {
                                            formattedValue = `(${value.slice(0, 3)})${value.slice(3, 6)}-${value.slice(6, 10)}`;
                                        }
                                    }
                                    setFormData({ ...formData, phoneNumbers: [formattedValue] });
                                }}
                                disabled={loading}
                                inputProps={{ maxLength: 13 }}
                                error={!!fieldErrors.phoneNumbers}
                                helperText={fieldErrors.phoneNumbers || "Phone number to be used for calls or notifications."}
                            />
                            <Box sx={{ display: "flex", alignItems: "center", mb: 2, gap: 1 }}>
                                {/* Exclusive toggle for Notify Only vs Call/Notify */}
                                <ToggleButtonGroup
                                    exclusive
                                    size="small"
                                    color={"primary"}
                                    value={formData.callContact}
                                    onChange={(_, val) => {
                                        if (val === null) return;
                                        setFormData({
                                            ...formData,
                                            callContact: val,
                                        });
                                    }}
                                    disabled={loading}
                                >
                                    <ToggleButton value={1}>Call / Notify</ToggleButton>
                                    <ToggleButton value={0}>Notify Only</ToggleButton>
                                </ToggleButtonGroup>
                                <Tooltip title="Call/Notify: caregiver is called when the alert button is pressed. Notify Only: caregiver receives notifications but is not called." placement="right" arrow>
                                    <IconButton size="small">
                                        <HelpOutlineIcon fontSize="small" color="action" />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </>
                    )}

                    {(!smartConnectUnit || formData.useApp === 1) && (
                        <FormControl component="fieldset" sx={{ mt: 2, mb: 2 }}>
                            <FormLabel component="legend">User Type</FormLabel>
                            <RadioGroup name="userType" value={formData.userType} onChange={handleChange}>
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                    <FormControlLabel value="View" control={<Radio />} label="View Only" />
                                    <Tooltip title="Can only view the status and location (if applicable) of the customer." placement="right" arrow>
                                        <IconButton size="small">
                                            <HelpOutlineIcon fontSize="small" color="action" />
                                        </IconButton>
                                    </Tooltip>
                                </Box>

                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                    <FormControlLabel value="Interactive" control={<Radio />} label="Interactive" />
                                    <Tooltip title="Can view customer status and location, plus update location and send chime commands (if applicable)." placement="right" arrow>
                                        <IconButton size="small">
                                            <HelpOutlineIcon fontSize="small" color="action" />
                                        </IconButton>
                                    </Tooltip>
                                </Box>

                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                    <FormControlLabel value="Admin" control={<Radio />} label="Admin" />
                                    <Tooltip title="Full access: Can view status and location, update location, send chime commands, and manage customer's caregivers." placement="right" arrow>
                                        <IconButton size="small">
                                            <HelpOutlineIcon fontSize="small" color="action" />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            </RadioGroup>
                        </FormControl>
                    )}

                    {/* Notification Preferences Section */}
                    <Box sx={{ mt: 3, mb: 2 }}>
                        <Divider sx={{ mb: 2 }} />
                        <Typography variant="h6" gutterBottom>
                            Notification Preferences
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Configure how this caregiver will receive notifications. You can change these settings later.
                        </Typography>

                        {loadingTemplates ? (
                            <Box display="flex" justifyContent="center" p={3}>
                                <CircularProgress />
                            </Box>
                        ) : (
                            <>
                                {Object.entries(
                                    availableTemplates.reduce((acc, template) => {
                                        if (!acc[template.Category]) {
                                            acc[template.Category] = [];
                                        }
                                        acc[template.Category].push(template);
                                        return acc;
                                    }, {})
                                ).map(([category, templates]) => (
                                    <Accordion key={category} >
                                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                                                {getCategoryLabel(category)}
                                            </Typography>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            {templates.map((template) => (
                                                <Box key={template.TemplateName} sx={{ mb: 2 }}>
                                                    <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 1 }}>
                                                        {template.Description}
                                                    </Typography>
                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, pl: 2 }}>
                                                        {formData.useApp === 1 && (
                                                            <>
                                                                <Tooltip title={deliveryMethodTooltips.email} arrow placement="top">
                                                                    <FormControlLabel
                                                                        control={
                                                                            <Switch
                                                                                checked={getPreferenceValue(template.TemplateName, category, 'EmailEnabled')}
                                                                                onChange={() => handleTogglePreference(template.TemplateName, category, 'EmailEnabled')}
                                                                                size="small"
                                                                            />
                                                                        }
                                                                        label="Email"
                                                                    />
                                                                </Tooltip>
                                                                <Tooltip title={deliveryMethodTooltips.push} arrow placement="top">
                                                                    <FormControlLabel
                                                                        control={
                                                                            <Switch
                                                                                checked={getPreferenceValue(template.TemplateName, category, 'PushEnabled')}
                                                                                onChange={() => handleTogglePreference(template.TemplateName, category, 'PushEnabled')}
                                                                                size="small"
                                                                            />
                                                                        }
                                                                        label="Push"
                                                                    />
                                                                </Tooltip>
                                                            </>
                                                        )}
                                                        {/* SMS option - only for smart_connect when user has SmartConnect unit and in alert category */}
                                                        {template.TemplateName.includes('smart_connect') && smartConnectUnit && category === 'alert' && (
                                                            <Tooltip title={deliveryMethodTooltips.sms} arrow placement="top">
                                                                <FormControlLabel
                                                                    control={
                                                                        <Switch
                                                                            checked={getPreferenceValue(template.TemplateName, category, 'SMSEnabled')}
                                                                            onChange={() => handleTogglePreference(template.TemplateName, category, 'SMSEnabled')}
                                                                            size="small"
                                                                        />
                                                                    }
                                                                    label="SMS"
                                                                />
                                                            </Tooltip>
                                                        )}
                                                    </Box>
                                                    <Divider sx={{ mt: 1 }} />
                                                </Box>
                                            ))}
                                        </AccordionDetails>
                                    </Accordion>
                                ))}
                            </>
                        )}
                    </Box>

                    <Box sx={{ display: "flex", justifyContent: "space-between", mt: 3 }}>
                        <Button variant="outlined" onClick={() => navigate(`/caregivers/${customerKey}`)} disabled={loading}>
                            Cancel
                        </Button>
                        <Button type="submit" variant="contained" color="primary" disabled={loading} startIcon={loading ? <CircularProgress size={20} /> : null}>
                            {loading ? "Adding..." : "Add Caregiver"}
                        </Button>
                    </Box>
                </Box>
            </Paper>
        </div>
    );
};

export default AddCaregiver;
